/**
 * Zeus collection magazine — GSAP ScrollTrigger panel swap + text reveal.
 */
(function () {
  "use strict";

  var MAX_GSAP_WAIT = 120;

  function waitForGsap(callback, onGiveUp) {
    if (typeof window.zeusWhenGsapReady === "function") {
      window.zeusWhenGsapReady(callback, onGiveUp);
      return;
    }
    var waitTicks = 0;
    function poll() {
      if (window.gsap && window.ScrollTrigger) {
        callback();
        return;
      }
      waitTicks += 1;
      if (waitTicks < MAX_GSAP_WAIT) {
        window.requestAnimationFrame(poll);
      } else if (typeof onGiveUp === "function") {
        onGiveUp();
      }
    }
    poll();
  }

  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function isMobile() {
    return window.matchMedia("(max-width: 768px)").matches;
  }

  function padIndex(n) {
    return String(n).padStart(2, "0");
  }

  function getScrollTargets(blocks, closing) {
    var scrollTargets = Array.prototype.slice.call(blocks);
    if (closing) {
      scrollTargets.push(closing);
    }
    return scrollTargets;
  }

  function activatePanel(panels, counter, idx, scrollTargets) {
    panels.forEach(function (panel, i) {
      panel.classList.toggle("is-active", i === idx);
    });
    if (scrollTargets) {
      scrollTargets.forEach(function (target) {
        var isActive = parseInt(target.getAttribute("data-index"), 10) === idx;
        target.classList.toggle("is-active", isActive);
        if (isActive) {
          target.setAttribute("aria-current", "true");
        } else {
          target.removeAttribute("aria-current");
        }
      });
    }
    if (counter) {
      counter.textContent = padIndex(idx + 1);
    }
  }

  function syncActivePanelFromScroll(blocks, closing, panels, counter) {
    var scrollTargets = getScrollTargets(blocks, closing);

    var activeIdx = 0;
    var marker = window.innerHeight * 0.45;

    scrollTargets.forEach(function (target) {
      var rect = target.getBoundingClientRect();
      if (rect.top <= marker && rect.bottom > marker) {
        activeIdx = parseInt(target.getAttribute("data-index"), 10) || activeIdx;
      }
    });

    activatePanel(panels, counter, activeIdx, scrollTargets);
  }

  function activateIndicator(indicators, idx) {
    indicators.forEach(function (indicator) {
      var indicatorIdx = parseInt(indicator.getAttribute("data-index"), 10);
      var isActive = indicatorIdx === idx;
      indicator.classList.toggle("is-active", isActive);
      if (isActive) {
        indicator.setAttribute("aria-current", "true");
      } else {
        indicator.removeAttribute("aria-current");
      }
    });
  }

  function killMagazineTriggers(root) {
    var ScrollTrigger = window.ScrollTrigger;
    if (!ScrollTrigger) return;
    ScrollTrigger.getAll()
      .filter(function (trigger) {
        return trigger.vars && trigger.vars.trigger && root.contains(trigger.vars.trigger);
      })
      .forEach(function (trigger) {
        trigger.kill();
      });
  }

  function revealFromTo(gsap, target, vars) {
    var fromVars = {
      opacity: vars.opacity != null ? vars.opacity : 0,
      x: vars.x || 0,
      y: vars.y || 0,
    };
    var toVars = {
      opacity: 1,
      x: 0,
      y: 0,
      duration: vars.duration || 0.7,
      ease: vars.ease || "power2.out",
      immediateRender: false,
      scrollTrigger: vars.scrollTrigger,
    };
    gsap.fromTo(target, fromVars, toVars);
  }

  function initMobileRail(root, blocks, closing) {
    if (!isMobile()) {
      return;
    }

    if (root.getAttribute("data-zcm-rail-init") === "true") {
      return;
    }

    var rail = root.querySelector(".zeus-coll-mag__txt-col");
    var indicators = root.querySelectorAll("[data-zcm-indicator]");
    var scrollTargets = getScrollTargets(blocks, closing);

    if (!rail || scrollTargets.length <= 1 || !indicators.length) {
      return;
    }

    root.setAttribute("data-zcm-rail-init", "true");

    var scrollBehavior = prefersReducedMotion() ? "auto" : "smooth";

    indicators.forEach(function (indicator) {
      indicator.addEventListener("click", function () {
        var idx = parseInt(indicator.getAttribute("data-index"), 10);
        if (Number.isNaN(idx)) {
          return;
        }

        var target = null;
        scrollTargets.forEach(function (slide) {
          if (parseInt(slide.getAttribute("data-index"), 10) === idx) {
            target = slide;
          }
        });

        if (!target) {
          return;
        }

        target.scrollIntoView({
          behavior: scrollBehavior,
          inline: "start",
          block: "nearest",
        });
        activateIndicator(indicators, idx);
      });
    });

    if (typeof IntersectionObserver === "undefined") {
      return;
    }

    var visibleRatios = new Map();

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            visibleRatios.set(entry.target, entry.intersectionRatio);
          } else {
            visibleRatios.delete(entry.target);
          }
        });

        var bestTarget = null;
        var bestRatio = -1;

        visibleRatios.forEach(function (ratio, target) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestTarget = target;
          }
        });

        if (bestTarget && bestRatio >= 0) {
          var activeIdx = parseInt(bestTarget.getAttribute("data-index"), 10);
          if (!Number.isNaN(activeIdx)) {
            activateIndicator(indicators, activeIdx);
          }
        }
      },
      {
        root: rail,
        threshold: [0.35, 0.55, 0.75],
      },
    );

    scrollTargets.forEach(function (target) {
      observer.observe(target);
    });
  }

  function initMagazine(root) {
    if (root.dataset.zcmInit === "true") {
      killMagazineTriggers(root);
    }
    root.dataset.zcmInit = "true";

    var blocks = root.querySelectorAll("[data-zcm-block]");
    var panels = root.querySelectorAll("[data-zcm-panel]");
    var counter = root.querySelector("[data-zcm-counter]");
    var closing = root.querySelector("[data-zcm-closing]");
    var scrollTargets = getScrollTargets(blocks, closing);

    if (!blocks.length) {
      return;
    }

    if (prefersReducedMotion() || isMobile()) {
      activatePanel(panels, counter, 0, scrollTargets);
      initMobileRail(root, blocks, closing);
      return;
    }

    function bindScrollTrigger() {
      var gsap = window.gsap;
      var ScrollTrigger = window.ScrollTrigger;
      if (!gsap || !ScrollTrigger) {
        return false;
      }
      gsap.registerPlugin(ScrollTrigger);
      killMagazineTriggers(root);

      scrollTargets.forEach(function (target, i) {
        var idx = parseInt(target.getAttribute("data-index"), 10);
        if (Number.isNaN(idx)) {
          idx = i;
        }

        ScrollTrigger.create({
          trigger: target,
          start: "top 55%",
          end: "bottom 45%",
          onEnter: function () {
            activatePanel(panels, counter, idx, scrollTargets);
          },
          onEnterBack: function () {
            activatePanel(panels, counter, idx, scrollTargets);
          },
          onToggle: function (self) {
            if (self.isActive) {
              activatePanel(panels, counter, idx, scrollTargets);
            }
          },
        });

        revealFromTo(gsap, target, {
          x: target === closing ? 0 : 40,
          y: target === closing ? 30 : 0,
          duration: 0.7,
          scrollTrigger: {
            trigger: target,
            start: "top 88%",
            toggleActions: "play none none none",
          },
        });
      });

      root.querySelectorAll(".zeus-coll-mag__block-copy").forEach(function (copy) {
        revealFromTo(gsap, copy, {
          y: 20,
          duration: 0.65,
          scrollTrigger: {
            trigger: copy,
            start: "top 90%",
            toggleActions: "play none none none",
          },
        });
      });

      syncActivePanelFromScroll(blocks, closing, panels, counter);
      ScrollTrigger.addEventListener("refreshInit", function () {
        syncActivePanelFromScroll(blocks, closing, panels, counter);
      });
      if (typeof window.zeusScrollTriggerRefresh === "function") {
        window.zeusScrollTriggerRefresh();
      } else {
        ScrollTrigger.refresh();
      }

      root.classList.add("is-gsap");
      return true;
    }

    waitForGsap(
      function () {
        if (!bindScrollTrigger()) {
          activatePanel(panels, counter, 0, scrollTargets);
        }
      },
      function () {
        activatePanel(panels, counter, 0, scrollTargets);
      },
    );
  }

  function boot() {
    document.querySelectorAll("[data-zeus-collection-magazine]").forEach(initMagazine);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  window.addEventListener("zeus:gsap-ready", function () {
    document.querySelectorAll("[data-zeus-collection-magazine]:not(.is-gsap)").forEach(initMagazine);
  });

  document.addEventListener("shopify:section:load", function (event) {
    var section = event.target;
    if (!section) {
      return;
    }
    var root = section.querySelector("[data-zeus-collection-magazine]");
    if (root) {
      root.dataset.zcmInit = "false";
      initMagazine(root);
    }
  });
})();
